import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchWarehouses, fetchWarehouseList } from '../fetch/warehouse';
import isNil from 'lodash/isNil';
import { JSON_PARSE_ERROR } from './consts';
import getAdapter from './getAdapter';

export const getWarehousesStatus = createAsyncThunk(
  'warehouses/getWarehousesStatus',
  (_, { rejectWithValue }) => fetchWarehouses()
      .then(({ data }) => ({ warehouses: data }))
      .catch((err) => rejectWithValue({ error: err })),
  {
    condition: (_, { getState }) => {
      const { warehouses } = getState();
      if (isNil(warehouses) || warehouses.isLoading) {
        return false;
      }
    },
  },
);

export const getWarehouseListStatus = createAsyncThunk(
  'warehouses/getWarehouseListStatus',
  (_, { rejectWithValue }) => fetchWarehouseList()
      .then(({ data }) => ({ warehouseList: data }))
      .catch((err) => rejectWithValue({ error: err })),
  {
    condition: (_, { getState }) => {
      const { warehouses } = getState();
      if (isNil(warehouses) || warehouses.isWarehouseListLoading) {
        return false;
      }
    },
  },
);

const initialState = {
  warehouses: [],
  warehouseList: [],
  isLoading: false,
  isWarehouseListLoading: false,
  error: null,
};

const warehousesAdapter = getAdapter({ idFieldName: 'id' });

const warehousesSlice = createSlice({
  name: 'warehouses',
  initialState: warehousesAdapter.getInitialState(),
  reducers: {
    setWarehouses: (state, { payload: { warehouses } }) => {
      warehousesAdapter.setAll(state, warehouses);
    },
  },
  extraReducers: {
    setWarehouses: (state, { payload: { warehouses } }) => {
      warehousesAdapter.setAll(state, warehouses);
    },
    [getWarehousesStatus.pending]: (state) => {
      state.isLoading = true;
    },
    [getWarehousesStatus.fulfilled]: (state, { payload: { warehouses } }) => {
      state.isLoading = false;
      state.warehouses = warehouses;
    },
    [getWarehousesStatus.rejected]: (state, { payload: { error } }) => {
      state.isLoading = false;
      state.error = error.message === JSON_PARSE_ERROR ? 'Error loading warehouses!' : error.message;
    },
    [getWarehouseListStatus.pending]: (state) => {
      state.isWarehouseListLoading = true;
    },
    [getWarehouseListStatus.fulfilled]: (state, { payload: { warehouseList } }) => {
      state.isWarehouseListLoading = false;
      state.warehouseList = warehouseList;
    },
    [getWarehouseListStatus.rejected]: (state, { payload: { error } }) => {
      state.isWarehouseListLoading = false;
      state.error = error.message === JSON_PARSE_ERROR ? 'Error loading warehouse list!' : error.message;
    },
  },
});

const selectors = warehousesAdapter.getSelectors(({ warehouses }) => warehouses);

export const getWarehousesIncludedDeleted = (state) => selectors.selectAll(state);

export const getWarehouses = (state) => getWarehousesIncludedDeleted(state)
  .filter(({ deleted }) => !deleted);

export const {
  setWarehouses,
} = warehousesSlice.actions;

export default warehousesSlice.reducer;
